import React from 'react';
import { Project, PROJECT_STATUS_OPTIONS, PROJECT_PRIORITY_OPTIONS } from '../../types/project';

export const PROJECT_STATUS_META: Record<Project['status'], { label: string; dot: string; chip: string }> = {
  planning: { label: 'Tervezés', dot: 'bg-gray-400', chip: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200' },
  active: { label: 'Aktív', dot: 'bg-sky-500', chip: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  completed: { label: 'Befejezett', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  paused: { label: 'Szünetel', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  cancelled: { label: 'Megszakítva', dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' }
};

export const PROJECT_PRIORITY_META: Record<Project['priority'], { label: string; dot: string; text: string }> = {
  low: { label: 'Alacsony', dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  medium: { label: 'Közepes', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  high: { label: 'Magas', dot: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400' },
  urgent: { label: 'Sürgős', dot: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400' }
};

export const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
export const fmtDate = (v: any) => { const d = toDate(v); return d ? d.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '–'; };
export const fmtShort = (v: any) => { const d = toDate(v); return d ? d.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }) : '–'; };
export const isProjectOverdue = (p: Project) => {
  const d = toDate(p.endDate);
  if (!d || p.status === 'completed' || p.status === 'cancelled') return false;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return d < t;
};
export const daysLeft = (p: Project) => {
  const d = toDate(p.endDate);
  if (!d) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - t.getTime()) / 86400000);
};
export const initials = (name?: string) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?';

export const ProjectStatusSelect: React.FC<{ value: Project['status']; onChange: (s: Project['status']) => void; size?: 'sm' | 'xs' }> = ({ value, onChange, size = 'sm' }) => {
  const meta = PROJECT_STATUS_META[value] || PROJECT_STATUS_META.planning;
  return (
    <span className="relative inline-flex items-center" onClick={(e) => e.stopPropagation()}>
      <span className={`pointer-events-none absolute left-2 w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      <select value={value} onChange={(e) => onChange(e.target.value as Project['status'])} style={{ backgroundImage: 'none' }}
        className={`appearance-none cursor-pointer rounded-full border-0 pl-5 pr-6 font-medium focus:ring-2 focus:ring-primary-500/40 ${meta.chip} ${size === 'xs' ? 'h-6 text-[11px]' : 'h-7 text-xs'}`} aria-label="Státusz">
        {PROJECT_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg className="pointer-events-none absolute right-2 w-3 h-3 opacity-60" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
    </span>
  );
};

export const ProjectAvatars: React.FC<{ names?: string[]; max?: number }> = ({ names = [], max = 4 }) => (
  <div className="flex -space-x-1.5">
    {names.slice(0, max).map((n, i) => (
      <span key={i} title={n} className="w-6 h-6 rounded-full ring-2 ring-[var(--crm-surface)] bg-gray-200 dark:bg-gray-700 text-[10px] font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center">{initials(n)}</span>
    ))}
    {names.length > max && <span className="w-6 h-6 rounded-full ring-2 ring-[var(--crm-surface)] bg-gray-100 dark:bg-gray-800 text-[10px] text-gray-500 flex items-center justify-center">+{names.length - max}</span>}
  </div>
);

export const priorityOptions = PROJECT_PRIORITY_OPTIONS;
